import { z } from 'zod';

const uuid = z.string().uuid();

/**
 * Why Albert refuses a request (#112): the month's quota is spent, the request asks for a
 * grade or a score (ADR-0010: Albert never grades), or the model's answer was unusable.
 */
export const albertRefusalReasonSchema = z.enum([
  'quota-exhausted',
  'no-grading',
  'invalid-output',
]);

/**
 * A refusal reason value.
 */
export type AlbertRefusalReason = z.infer<typeof albertRefusalReasonSchema>;

/**
 * What a teacher asks Albert for: a lesson plan, a document (questions, worksheet with rubric,
 * rubric) or a draft (a report-card observation or a message to a family).
 */
export const albertKindSchema = z.enum(['lesson-plan', 'material', 'draft']);

/**
 * A request kind value.
 */
export type AlbertKind = z.infer<typeof albertKindSchema>;

/**
 * The documents Albert produces for a topic.
 */
export const albertMaterialKindSchema = z.enum(['questions', 'worksheet', 'rubric']);

/**
 * A material kind value.
 */
export type AlbertMaterialKind = z.infer<typeof albertMaterialKindSchema>;

/**
 * The level a document is pitched at; every document also carries differentiation notes for
 * the other two.
 */
export const albertLevelSchema = z.enum(['basico', 'medio', 'avanzado']);

/**
 * A level value.
 */
export type AlbertLevel = z.infer<typeof albertLevelSchema>;

/**
 * The drafts Albert writes from a teacher's bullet points.
 */
export const albertDraftKindSchema = z.enum(['observation', 'family-message']);

/**
 * A draft kind value.
 */
export type AlbertDraftKind = z.infer<typeof albertDraftKindSchema>;

const focus = z.string().trim().max(300).optional();

/**
 * Body of `POST /albert/lesson-plans`: the topic of the plan de estudios and the group it is
 * planned for (the pair `D-par`), with an optional focus in the teacher's words.
 */
export const albertLessonPlanInputSchema = z.object({
  topicId: uuid,
  groupId: uuid,
  focus,
});

/**
 * Input of the lesson-plan route.
 */
export type AlbertLessonPlanInput = z.input<typeof albertLessonPlanInputSchema>;

/**
 * Query of `GET /albert/lesson-plans`: the topic.
 */
export const albertLessonPlansQuerySchema = z.object({ topicId: uuid });

/**
 * Body of `PUT /albert/lesson-plans/{id}`: the edited plan.
 */
export const albertLessonPlanUpdateSchema = z.object({
  title: z.string().trim().min(3).max(200).optional(),
  content: z.string().trim().min(20).max(20_000),
});

/**
 * Input of the lesson-plan edit route.
 */
export type AlbertLessonPlanUpdate = z.input<typeof albertLessonPlanUpdateSchema>;

/**
 * Body of `POST /albert/materials`: the topic, the group, what kind of document and at which
 * level, with an optional focus.
 */
export const albertMaterialInputSchema = z.object({
  topicId: uuid,
  groupId: uuid,
  kind: albertMaterialKindSchema,
  level: albertLevelSchema.default('medio'),
  focus,
});

/**
 * Input of the materials route.
 */
export type AlbertMaterialInput = z.input<typeof albertMaterialInputSchema>;

/**
 * Body of `POST /albert/drafts`: the pair, the kind of text, the teacher's bullet points and
 * an optional first name (ADR-0010: the first name is the only personal datum that reaches the
 * model; surnames, documents and phone numbers are refused).
 */
export const albertDraftInputSchema = z.object({
  groupId: uuid,
  subjectId: uuid,
  kind: albertDraftKindSchema,
  bullets: z.array(z.string().trim().min(2).max(240)).min(1).max(12),
  studentFirstName: z.string().trim().min(2).max(40).optional(),
  tone: z.enum(['formal', 'cercano']).default('cercano'),
});

/**
 * Input of the drafts route.
 */
export type AlbertDraftInput = z.input<typeof albertDraftInputSchema>;

/**
 * Query of `GET /staff/tenants/{id}/albert-usage`: the month (`YYYY-MM`, the current one by
 * default).
 */
export const albertUsageQuerySchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM')
    .optional(),
});

/**
 * The teacher's monthly quota of Albert requests and what is left of it.
 */
export type AlbertQuotaView = {
  month: string;
  monthly: number;
  used: number;
  remaining: number;
};

/**
 * Tokens, latency and USD of the call behind an answer.
 */
export type AlbertUsageView = {
  inputTokens: number;
  outputTokens: number;
  latencyMs: number;
  costUsd: number;
};

/**
 * A lesson plan saved on a topic, as the teacher reads and edits it.
 */
export type AlbertLessonPlanView = {
  id: string;
  topicId: string;
  topicTitle: string;
  groupId: string | null;
  subjectId: string | null;
  gradeId: string | null;
  periodPosition: number;
  title: string;
  content: string;
  modelId: string;
  teacherUserId: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * What `GET /albert/lesson-plans?topicId` returns: the caller's plans on the topic (newest
 * first) and the quota left, so the screen shows "N de M este mes" before the first request.
 */
export type AlbertLessonPlansView = {
  items: AlbertLessonPlanView[];
  quota: AlbertQuotaView;
};

/**
 * What `POST /albert/lesson-plans` returns: the saved plan, the call's usage and the quota
 * left.
 */
export type AlbertLessonPlanResult = {
  plan: AlbertLessonPlanView;
  usage: AlbertUsageView;
  quota: AlbertQuotaView;
};

/**
 * A document Albert generated, with a presigned link to download it (the link lasts minutes;
 * `GET /albert/materials/{id}` issues a fresh one).
 */
export type AlbertMaterialView = {
  id: string;
  topicId: string;
  topicTitle: string;
  kind: AlbertMaterialKind;
  level: AlbertLevel;
  title: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  fileKey: string;
  downloadUrl: string;
  expiresAt: string;
  createdAt: string;
};

/**
 * What `POST /albert/materials` returns.
 */
export type AlbertMaterialResult = {
  material: AlbertMaterialView;
  usage: AlbertUsageView;
  quota: AlbertQuotaView;
};

/**
 * What `POST /albert/drafts` returns: the text, ready to paste and edit.
 */
export type AlbertDraftResult = {
  kind: AlbertDraftKind;
  text: string;
  usage: AlbertUsageView;
  quota: AlbertQuotaView;
};

/**
 * What ClassGO reads per school and month: every teacher who asked Albert something, with
 * requests by kind, refusals, tokens and USD at the model's price.
 */
export type AlbertTenantUsage = {
  month: string;
  modelId: string;
  monthlyQuota: number;
  activeTeachers: number;
  teachers: {
    teacherUserId: string | null;
    name: string;
    requests: number;
    lessonPlans: number;
    materials: number;
    drafts: number;
    refused: number;
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
  }[];
  totals: { requests: number; inputTokens: number; outputTokens: number; costUsd: number };
};
